import { useEffect, useMemo, useRef, useState } from "react"
import sl from "../../../../components/startups-landing/startups-landing-sections.module.css"
import styles from "./how-it-works.module.css"
import pdfIconSrc from "./assets/icon-file-pdf.svg"
import kateAvatarSrc from "../find-work/assets/kate-avatar.png"
import alexAvatarSrc from "../../../../components/startups-landing/assets/client-avatar.png"

function FadeUp({ children, className = "", delayClass = "" }) {
	const ref = useRef(null)
	useEffect(() => {
		const el = ref.current
		if (!el) return
		const obs = new IntersectionObserver(
			(entries) => {
				entries.forEach((e) => {
					if (e.isIntersecting) e.target.classList.add(sl.fadeUpVisible)
				})
			},
			{ threshold: 0.12 }
		)
		obs.observe(el)
		return () => obs.disconnect()
	}, [])
	return (
		<div ref={ref} className={`${sl.fadeUp} ${delayClass} ${className}`}>
			{children}
		</div>
	)
}

const STEPS = [
	{
		title: "Build profile",
		body: "Import from LinkedIn, upload a resume, or build from scratch. Your profile unlocks the Techcore Network, a curated pool of growing companies hiring right now. Private by default and shared only when there's a match worth your attention.",
	},
	{
		title: "Talk to Kate",
		body: "Describe your ideal role and preferences in plain language. Share your goals, non-negotiables, and what matters beyond the job title. Kate reads your profile, searches the Techcore Network, and comes back with the roles worth your attention.",
	},
	{
		title: "Get matched",
		body: "The right roles come to you, not the other way around. Ready to move forward? Tell Kate and she'll arrange an introduction. Have questions first? Ask her anything and she'll get answers from the hiring team before you decide.",
	},
]

function BuildProfileIllustration() {
	return (
		<div className={styles.illuCanvas}>
			<div className={styles.illuUpload}>
				<div className={styles.uploadDropzone}>
					<div className={styles.uploadTitle}>Drop your file here</div>
				</div>
				<div className={styles.uploadFileChip} aria-hidden>
					<img src={pdfIconSrc} alt="" />
					<div className={styles.fileName}>My resume.pdf</div>
				</div>
				<div className={styles.uploadingState} aria-hidden>
					<span>Uploading...</span>
					<div className={styles.uploadProgressTrack}>
						<div className={styles.uploadProgressFill} />
					</div>
				</div>
				<div className={styles.uploadedState} aria-hidden>
					<div className={styles.uploadedCheck}>✓</div>
					<span>File uploaded</span>
				</div>
			</div>
		</div>
	)
}

const CHAT_SCRIPT = [
	{
		id: "a1",
		from: "alex",
		text: "Hey Kate, I'm open to new opportunities.",
	},
	{
		id: "k1",
		from: "kate",
		text: "Great. Any preference on location or role level?",
	},
	{
		id: "a2",
		from: "alex",
		text: "Senior/Lead engineer, remote-first in EU.",
	},
	{
		id: "k2",
		from: "kate",
		text: "Perfect. I'll start matching now.",
	},
]

function TalkToKateIllustration() {
	const [chatIndex, setChatIndex] = useState(2)

	useEffect(() => {
		const t = setInterval(() => {
			setChatIndex((prev) => (prev >= CHAT_SCRIPT.length ? 2 : prev + 1))
		}, 2500)
		return () => clearInterval(t)
	}, [])

	const visibleMessages = useMemo(() => CHAT_SCRIPT.slice(Math.max(0, chatIndex - 2), chatIndex), [chatIndex])

	return (
		<div className={styles.illuCanvas}>
			<div className={styles.illuChat}>
				<div className={styles.chatHeader}>
					<img src={kateAvatarSrc} alt="" className={styles.kateAvatar} />
					<div>
						<div className={styles.chatName}>Kate</div>
						<div className={styles.chatStatus}>Career ally</div>
					</div>
				</div>
				<div className={styles.chatWindow}>
					{visibleMessages.map((msg) => (
						<div
							key={msg.id}
							className={`${styles.chatMsgRow} ${msg.from === "kate" ? styles.chatMsgRowKate : styles.chatMsgRowAlex}`}
						>
							{msg.from === "kate" ? (
								<>
									<img src={kateAvatarSrc} alt="" className={styles.chatMsgAvatar} draggable={false} />
									<div className={`${styles.chatMsg} ${styles.chatMsgKate}`}>{msg.text}</div>
								</>
							) : (
								<>
									<div className={`${styles.chatMsg} ${styles.chatMsgAlex}`}>{msg.text}</div>
									<img src={alexAvatarSrc} alt="" className={styles.chatMsgAvatar} draggable={false} />
								</>
							)}
						</div>
					))}
				</div>
			</div>
		</div>
	)
}

function GetMatchedIllustration() {
	const jobs = [
		{
			id: "j1",
			title: "Head of Engineering",
			meta: "Password Security · Remote · $280-360k",
			match: "95%",
		},
		{
			id: "j2",
			title: "Senior iOS Engineer",
			meta: "Consumer Fintech · London · £100-150k",
			match: "89%",
		},
		{
			id: "j3",
			title: "Staff Fullstack",
			meta: "Fintech Platform · Amsterdam · €160-220k",
			match: "92%",
		},
		{
			id: "j4",
			title: "Senior Backend Engineer",
			meta: "Digital Banking · London · £130-180k",
			match: "98%",
		},
		{
			id: "j5",
			title: "Principal Engineer",
			meta: "Payments Infra · Remote · $320-400k",
			match: "93%",
		},
	]

	return (
		<div className={styles.illuCanvas}>
			<div className={styles.illuMatch}>
				<div className={styles.matchHeader}>
					<div className={styles.matchTitleLabel}>New matches</div>
				</div>
				<div className={styles.matchListViewport}>
					<div className={styles.matchListTrack}>
						{jobs.slice(-3).concat(jobs).map((job, i) => (
							<div className={styles.jobRow} key={`${job.id}-${i}`}>
								<div>
									<div className={styles.jobTitle}>{job.title}</div>
									<div className={styles.jobMeta}>{job.meta}</div>
								</div>
								<div className={styles.jobScore}>{job.match}</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	)
}

const TalentsHowItWorks = () => {
	return (
		<section id="how-it-works" className={styles.howItWorks}>
			<div className={`container ${sl.sectionContainer}`}>
				<FadeUp>
					<div className={styles.howHeader}>
						<div className={sl.labelCenter}>How it works</div>
						<h2>Build once. Match smart. Move fast.</h2>
					</div>
				</FadeUp>
				<div className={styles.markerGrid} aria-hidden>
					<div className={styles.markerConnector} />
					{STEPS.map((step, i) => (
						<div key={`node-${step.title}`} className={styles.stepMarker}>
							<div
								className={`${sl.howCircle} ${
									i === 0 ? sl.howCircleBlue : i === 1 ? sl.howCirclePurple : sl.howCircleTeal
								}`}
							>
								<span>{String(i + 1).padStart(2, "0")}</span>
							</div>
						</div>
					))}
				</div>
				<div className={styles.howGrid}>
					{STEPS.map((step, i) => (
						<FadeUp key={step.title} delayClass={i === 1 ? sl.fadeUp2 : i === 2 ? sl.fadeUp3 : ""}>
							<div className={styles.stepCard}>
								<div className={styles.stepMobileMarker} aria-hidden>
									<div className={`${sl.howCircle} ${
										i === 0 ? sl.howCircleBlue : i === 1 ? sl.howCirclePurple : sl.howCircleTeal
									}`}>
										<span>{String(i + 1).padStart(2, "0")}</span>
									</div>
								</div>
								<div className={styles.stepHead}>
									<h3>{step.title}</h3>
								</div>
								<p className={styles.stepBody}>{step.body}</p>
								{i === 0 ? <BuildProfileIllustration /> : null}
								{i === 1 ? <TalkToKateIllustration /> : null}
								{i === 2 ? <GetMatchedIllustration /> : null}
							</div>
						</FadeUp>
					))}
				</div>
			</div>
		</section>
	)
}

export default TalentsHowItWorks
